import React from 'react';
import {
    useState, useMemo, useEffect, useRef,
} from 'react';
import { Paper, Tooltip } from '@mui/material';
import { faAward as awardIcon } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table } from 'rsuite';
import { useAvailableHeight } from '../../hooks';
import { dynamicSort } from '../../services/sort';
import { DataviewTooltip, DataviewValue } from '..';
import columnWidthMap from './columnWidthMap';
import './DataviewTableContent.scss';

const { Cell, Column, HeaderCell } = Table;

/** @type {CommonClient2.SortType} */
let defaultSortType;
// eslint-disable-next-line prefer-const
defaultSortType = 'asc';

const CheckCell = ({
    rowData, isRowSelectionDisabled, onChange, checkedKeys, dataKey, ...props
}) => (
    <Cell
        {...props}
        style={{ padding: 0 }}
    >
        <div style={{ lineHeight: '46px' }}>
            <Checkbox
                value={rowData[dataKey]}
                inline
                onChange={onChange}
                disabled={!!isRowSelectionDisabled}
                checked={checkedKeys.some((item) => item.toString() === rowData[dataKey].toString())}
            />
        </div>
    </Cell>
);

// eslint-disable-next-line react/display-name
const mapNonDataviewColumn = (t) => (/** @type {CommonClient2.DataviewTableStaticColumn} */ {
    key, tKey, isTooltip, ...rest
}) => {
    if (isTooltip) {
        return (
            <Column
                {...rest}
                key={key}
            >
                <HeaderCell>{t(tKey)}</HeaderCell>
                <Cell dataKey={key}>
                    {(rowData) => (
                        <div
                            className="core--tooltip-cell"
                        >
                            <Tooltip title={rowData[key]}>
                                <span>{rowData[key]}</span>
                            </Tooltip>
                        </div>
                    )}
                </Cell>
            </Column>
        );
    }

    return (
        <Column
            {...rest}
            key={key}
        >
            <HeaderCell>{t(tKey)}</HeaderCell>
            <Cell dataKey={key} />
        </Column>
    );
};

/**
 * @template T
 * @param {{
 *  data: T[];
 *  isDataviewFixed?: boolean;
 *  tableId?: string;
 *  columns: CommonClient2.DataviewTableStaticColumn[];
 *  dataviewColumns: CommonClient2.DataviewTableColumn[];
 *  onRowClick?: (rowData: T, event: *) => void;
 *  isRowsSelectable?: boolean;
 *  isRowSelectionDisabled?: boolean;
 *  selectedItems: string[];
 *  nameColumn: string;
 *  onChange?: (ids: number[]) => {};
 *  fillAvailableHeight: boolean;
 *  ignoredKeys: string[];
 * } & import('rsuite-table').TableProps} param0
 */
const DataviewTableContent = ({
    data,
    isDataviewFixed = false,
    columns = [],
    dataviewColumns = [],
    nameColumn,
    isRowsSelectable,
    isRowSelectionDisabled,
    selectedItems,
    onChange,
    fillAvailableHeight,
    height,
    ignoredKeys,
    ...others
}) => {
    const ref = useRef(null);
    const [firstColumn] = columns;
    const [sortType, setSortType] = useState(defaultSortType);
    const [sortColumn, setSortColumn] = useState(firstColumn?.key || '');
    const [checkedKeys, setCheckedKeys] = useState(selectedItems || []);

    const sortKey = sortType === 'desc' ? `-${sortColumn}` : sortColumn;
    const sortedData = useMemo(() => data?.sort(dynamicSort(sortKey)), [JSON.stringify(data), sortType, sortColumn]);
    const { t } = useTranslation('table');

    useEffect(() => setCheckedKeys(selectedItems || []), [JSON.stringify(selectedItems)]);
    /**
     * @param {string} newSortColumn
     * @param {CommonClient2.SortType} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    const onSelectionChange = (keys) => {
        const stringifiedKeys = keys.map((k) => k.toString());
        setCheckedKeys(stringifiedKeys);
        if (onChange) {
            onChange(stringifiedKeys);
        }
    };

    const handleCheckAll = (_, checked) => {
        // @ts-ignore
        const keys = checked ? data.map((item) => item.id) : [];
        onSelectionChange(keys);
    };

    const handleCheck = (value, checked) => {
        const keys = checked ? [...checkedKeys, value] : checkedKeys.filter((item) => item !== value);
        onSelectionChange(keys);
    };

    const checked = checkedKeys.length === data?.length;
    const indeterminate = !!checkedKeys.length && checkedKeys.length < data?.length;

    const beforeNameColumns = (columns || []).filter((c) => c.isBeforeName);
    const beforeDataviewColumns = (columns || []).filter((c) => !c.isBeforeName && c.isBeforeDataview);
    const afterDataviewColumns = (columns || []).filter((c) => !c.isBeforeName && !c.isBeforeDataview);

    const nameColumnFound = (dataviewColumns || []).find((dv) => dv.key.includes(nameColumn));
    const autoHeightValue = useAvailableHeight(ref, fillAvailableHeight);

    return (
        <Paper
            elevation={2}
            ref={ref}
            className="core--table-dataview-content"
        >
            <Table
                data={sortedData}
                sortType={sortType}
                sortColumn={sortColumn}
                // @ts-ignore
                onSortColumn={handleSortColumn}
                rowClassName={(rowData) => `core--table-dataview-row ${rowData?.isAwarded ? 'awarded' : ''}`}
                height={height || autoHeightValue}
                {...others}
            >
                {
                    isRowsSelectable && (
                        <Column
                            width={50}
                            align="center"
                        >
                            <HeaderCell className="header-checkbox-cell" >
                                <Checkbox
                                    inline
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    disabled={!!isRowSelectionDisabled}
                                    onChange={handleCheckAll}
                                />
                            </HeaderCell>
                            <CheckCell
                                dataKey="id"
                                checkedKeys={checkedKeys}
                                onChange={handleCheck}
                                isRowSelectionDisabled={isRowSelectionDisabled}
                                rowData={null}
                            />
                        </Column>
                    )
                }
                {(beforeNameColumns).map(mapNonDataviewColumn(t))}
                {
                    nameColumnFound && (
                        <Column
                            fixed="left"
                            width={100}
                            {...nameColumnFound}
                            minWidth={175}
                        >
                            <HeaderCell>{t(`columns.${nameColumnFound.tKey}`)}</HeaderCell>
                            <Cell dataKey={nameColumnFound.key}>
                                {(rowData) => (
                                    <div className="name-column">
                                        {rowData?.isAwarded && (
                                            <div className="award-icon">
                                                <FontAwesomeIcon
                                                    icon={awardIcon}
                                                    size="lg"
                                                    color="#00862c"
                                                />
                                            </div>
                                        )}
                                        <DataviewTooltip
                                            dataviewColumns={dataviewColumns}
                                            dataview={rowData}
                                            label={rowData[nameColumnFound.key]}
                                            ignoredKeys={ignoredKeys}
                                        />
                                    </div>
                                )}
                            </Cell>
                        </Column>
                    )
                }
                {(beforeDataviewColumns).map(mapNonDataviewColumn(t))}
                {(dataviewColumns || []).filter((dv) => dv.visible && !dv.key.includes(nameColumn))
                    .map(({
                        key, tKey, dataValueType, ...rest
                    }) => (
                        <Column
                            fixed={isDataviewFixed ? 'left' : undefined}
                            {...rest}
                            key={key}
                            minWidth={columnWidthMap[dataValueType]}
                        >
                            <HeaderCell>{t(tKey)}</HeaderCell>
                            <Cell dataKey={key}>
                                {(rowData) => (
                                    <DataviewValue
                                        dataValueType={dataValueType}
                                        value={rowData[key]}
                                    />
                                )}
                            </Cell>
                        </Column>
                    ))}
                {(afterDataviewColumns || []).map(mapNonDataviewColumn(t))}
            </Table>
        </Paper>
    );
};
export default DataviewTableContent;
